/**
 * Module dependencies.
 */

import { ReactNode, useCallback, useMemo, useReducer } from 'react';
import { SortContext } from './context';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import qs from 'qs';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  omitUrl?: boolean;
};

/**
 * `State` type.
 */

type State = {
  sort: string | any;
  submittedSort: string | any;
};

/**
 * Action types.
 */

const actionTypes = {
  clearSort: 'CLEAR_SORT',
  resetSort: 'RESET_SORT',
  setSort: 'SET_SORT',
  submitSort: 'SUBMIT_SORT'
};

/**
 * Reducer.
 */

function reducer(state: State, { type, value }) {
  switch (type) {
    case actionTypes.setSort: {
      return {
        ...state,
        sort: value
      };
    }

    case actionTypes.clearSort: {
      return {
        sort: null,
        submittedSort: null
      };
    }

    case actionTypes.resetSort: {
      return {
        ...state,
        sort: state?.submittedSort
      };
    }

    case actionTypes.submitSort: {
      return {
        ...state,
        submittedSort: state?.sort
      };
    }

    default:
      return state;
  }
}

/**
 * `useInitialState` hook.
 */

function useInitialState(omitUrl: boolean): State {
  const router = useRouter();
  const query = omitUrl ? null : qs.parse(Object(router.query));

  return {
    sort: query?.sort ?? null,
    submittedSort: query?.sort ?? null
  };
}

/**
 * `FiltersListProvider` provider.
 */

const FiltersListProvider = ({ children, omitUrl }: Props) => {
  const initialState = useInitialState(omitUrl);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { sort, submittedSort } = state;

  // On set sort value.
  const handleSet = useCallback(
    (value: string) => {
      const dispatchedAction = {
        type: actionTypes.setSort,
        value
      };

      dispatch(dispatchedAction);

      return reducer(state, dispatchedAction);
    },
    [state]
  );

  // On submit sort value.
  const handleSubmit = useCallback(() => {
    const dispatchedAction = {
      type: actionTypes.submitSort,
      value: null
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // On clear sort.
  const handleClear = useCallback(() => {
    const dispatchedAction = {
      type: actionTypes.clearSort,
      value: null
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // On reset sort.
  const handleReset = useCallback(() => {
    const dispatchedAction = {
      type: actionTypes.resetSort,
      value: null
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // Check if value is selected.
  const handleIsSelected = useCallback(
    (value: string) => {
      const sortState = sort ?? null;

      return sortState === value;
    },
    [sort]
  );

  const normalizedSelectedSort = useMemo(() => {
    return isEmpty(sort) ? null : sort;
  }, [sort]);

  const normalizeSubmittedSort = useMemo(() => {
    return isEmpty(submittedSort) ? null : submittedSort;
  }, [submittedSort]);

  return (
    <SortContext.Provider
      value={{
        isSortSelected: handleIsSelected,
        omitUrl,
        onClear: handleClear,
        onSet: handleSet,
        onSubmit: handleSubmit,
        reset: handleReset,
        sort: normalizedSelectedSort,
        submittedSort: normalizeSubmittedSort
      }}
    >
      {children}
    </SortContext.Provider>
  );
};

/**
 * Export `FiltersListProvider` provider.
 */

export default FiltersListProvider;
